<template>
    <div class="single-shop">
        <div class="container">
            <div class="row">
                <div class="col-lg-6 col-md-12">
                    <div class="single-shop__ single-shop__first">
                        <div class="single-shop__content">
                            <div class="single-shop__image space32">
                                <ImagePreview 
                                    :src="data.cover"
                                />
                            </div>
                            <TitleDescription
                                :title="data.title"
                                :description="data.description"
                            />
                            <Tags
                                v-if="tagsExists"
                                :tags="data.tags"   
                                title="Product Types" 
                                type="simple"
                            />
                        </div>
                    </div>
                </div>
                <div class="col-lg-6 col-md-12 mt-lg-0 mt-3">
                    <div class="single-shop__">
                        <div class="single-shop__content single-shop__content__width-footer">
                            <MatchProgress 
                                v-if="matchingScore && !shopOwner"
                                :percent="matchingScore"
                                class="space20"
                            />
                            <About 
                                key="alexa"
                                icon="rank-yellow"
                                title="Alexa Rank:"
                                :value="data.alexa_rank"
                            />
                            <About 
                                key="monthly-sales"
                                icon="monthly-sales"
                                title="Monthly Sales:"
                                :value="monthlySalesRange"
                                :currency="true"
                            />
                            <About 
                                key="category"
                                icon="category-shop-icon"
                                title="Category:"
                                :value="data.category"
                            />
                            <About 
                                key="location"
                                icon="location"
                                title="Country:"
                                :value="data.country_code"
                            />
                            <div class="single-shop__options-wrapper">
                                <Options :options="data.crossprom_options"/>
                            </div>
                        </div>
                        <div class="single-shop__footer">
                            <Button v-if="!shopOwner" class="single-shop__action-button" data-type="primary" @click.native = "openModal">
                                Send Messsage
                            </Button>
                            <Button
                                v-else
                                @click.native="editShop"
                                class="single-shop__action-button c24 eina-semibold p2b"
                                type="gray">
                                    <Icon icon="edit-icon-linear" class="mr-2"/>
                                    <span>Edit Shop Info</span>    
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ImagePreview from '@/components/Ecommerce/ImagePreview'
import TitleDescription from '@/components/Ecommerce/TitleDescription'
import Tags from '@/components/Ecommerce/Tags'
import MatchProgress from '@/components/Ecommerce/MatchProgress'
import SocialGroup from '@/components/Ecommerce/SocialGroup'
import About from '@/components/Ecommerce/About'
import Options from '@/components/Ecommerce/Options'
import Social from '@/components/Ecommerce/Social'
import Button from '@/components/Theme/Button'
import { useMatchingScore, useRouter, useShops, useStore, useUserId, useRange } from '../../core/hooks'
import { computed, ref, watch, onMounted } from '@vue/composition-api'
import Icon from '@/components/Theme/Icon'
export default {
    name: 'SingleShop',
    components: {
        ImagePreview,
        TitleDescription,
        Tags,
        MatchProgress,
        SocialGroup,
        About,
        Options,
        Social,
        Button,
        Icon
    },
    setup(props, {root}) {
        const { route, router } = useRouter()
        const { state, store } = useStore()
        const { monthlySalesMatch, alexaRankMatch, categoryMatch } = useMatchingScore()
        const {data, getShopData } = useShops()
        const {userId} = useUserId()
        const monthlySalesMatchScore = ref()
        const alexaRankMatchScore = ref()
        const categoryMatchScore = ref()
        const { monthlySalesRange, setMonthlySalesRange } = useRange()

        onMounted(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' })
        })

        watch(route, async (route) => {
            await getShopData(route.params.shopId)
            if(!state.value.myShops.length) {
                return
            }
            setMonthlySalesRange(data.value.estimated_monthly_sales)
            monthlySalesMatchScore.value = monthlySalesMatch(state.value.myShops[0].estimated_monthly_sales, data.value.estimated_monthly_sales)
            alexaRankMatchScore.value = alexaRankMatch(state.value.myShops[0].alexa_rank, data.value.alexa_rank)
            categoryMatchScore.value = categoryMatch(state.value.myShops[0].category, data.value.category)
        }, { immediate: true })
        
        const tagsExists = computed(() => data.value && data.value.tags && data.value.tags.length > 0)
        const matchingScore = computed(() => monthlySalesMatchScore.value + alexaRankMatchScore.value + categoryMatchScore.value)
        const shopOwner = computed(() => userId === data.value.owner_id)

        const editShop = () => {
            root.$sendMixpanelEvent('Edit Shop Clicked', {
                'Single Shop': data.value.title
            })

            root.$ga.event('Single Shop', 'Edit Shop Clicked', `
                Single Shop: ${data.value.title}
            `)
            router.push(`/edit-shop/${data.value.id}`).catch(() => {})
        }
        
        const openModal = () => {
            if(!userId) {
                return store.commit('TOGGLE_MODAL', ['sign-up', {
                    openedFrom: 'Single Shop Send Message'
                }])            
            }
            store.commit('TOGGLE_MODAL', ['send-message-modal', {
                openedFrom: 'Single Shop Page',
                shopData: data.value
            }])
        }

        return {
            data,
            tagsExists,
            matchingScore,
            shopOwner,
            monthlySalesRange,
            editShop,
            openModal
        }
    },
}
</script>

<style lang="scss" scoped>
$space32: 32px;
$space24: 24px;
$space20: 20px;
$space16: 16px;
$padding: 0 40px;
$white: #ffffff;
$border_radius: 12px;
$height: 676px;
$shadow: 0px 12px 12px -6px rgba(113, 128, 150, 0.16), 0px 0px 1px rgba(113, 128, 150, 0.2);
$top_shadow: 0px -6px 12px -6px rgba(113, 128, 150, 0.12), 0px 0px 1px rgba(113, 128, 150, 0.16);
.single-shop {
    --description-margin-bottom: 16px;
    &__first {
        padding-bottom: 40px;
        overflow-y: auto;
    }
    &__ {
        height: $height;
        background: white;
        border-radius: $border_radius;
        box-shadow: $shadow;
        display: flex;
        flex-direction: column;
        padding-top: 40px;
        @media (max-width: 976px) {
            height: auto;
            &.single-shop__first {
                padding-bottom: 32px;
            }
            .single-shop__options-wrapper {
                padding-bottom: 32px;
            }
        }
    }
    &__image {
        height: 240px;
    }
    &__content {
        padding: $padding;
        &__width-footer {
            flex: 1 1 auto;
            overflow-y: auto;
            position: relative;
        }
    }
    &__footer {
        display: flex;
        justify-content: center;
        align-items: center;
        box-shadow: $top_shadow;
        height: 88px;
    }
    &__action-button {
        width: 100%;
        max-width: 383px;
        height: 48px;
    }
    .space24 {
        margin-bottom: $space24;
    }
    .space20 {
        margin-bottom: $space20;
    }
    .space16 {
        margin-bottom: $space16;
    }
    .space32 {
        margin-bottom: $space32;
    }
}
</style>>
